import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, EmptyValue, Form } from '@/components/ui';
import { Vehicle, VehicleProfile } from '../../../types/Vehicle';
import vehicleService, { fieldErrorsOf } from '../../../services/vehicleService';
import vehicleProfileService from '../../../services/vehicleProfileService';
import { roadTypeOf, ROAD_TYPE_LABEL } from '../utils/equipmentDisplay';
import { formatDate } from '../../../utils/wrapDate';
import { serviceAreaLabel } from '../utils/equipmentDisplay';
import EquipmentInsuranceCard from './EquipmentInsuranceCard';

interface EquipmentAboutTabProps {
  vehicle: Vehicle;
  canEditUnit: boolean;
  canEditRoadClass: boolean;
  /** Manage Repairs: Next CVIP (EQ-01). */
  canEditCvip: boolean;
  /** Fields are read-only until the page is put into edit mode. */
  editing: boolean;
  onUpdated: (vehicle: Vehicle) => void;
  /** Leave edit mode; the page asks first when there are unsaved changes. */
  onCancel: () => void;
  onSaved: () => void;
  /** Tells the page whether anything typed is unsaved, so leaving can ask first (EQ-20). */
  onDirtyChange: (dirty: boolean) => void;
}

interface Draft {
  description: string;
  vehicleProfileId: number | null;
  plateNumber: string;
  numberOfAxles: string;
  comments: string;
  gpsModemId: string;
  roadType: 'On-Road' | 'Off-Road' | '';
  nextCVIP: string;
}

function draftOf(vehicle: Vehicle): Draft {
  return {
    description: vehicle.description || '',
    vehicleProfileId: vehicle.vehicleProfileId ?? vehicle.vehicleProfile?.id ?? null,
    plateNumber: vehicle.plateNumber || '',
    numberOfAxles: vehicle.numberOfAxles != null ? String(vehicle.numberOfAxles) : '',
    comments: vehicle.comments || '',
    gpsModemId: vehicle.gpsModemId || '',
    roadType: roadTypeOf(vehicle) ?? '',
    nextCVIP: formatDate(vehicle.nextCVIP) ?? ''
  };
}

function text(value: string | number | null | undefined): React.ReactNode {
  if (value == null || value === '') return <EmptyValue />;
  return String(value);
}

function makeModelYear(vehicle: Vehicle): React.ReactNode {
  const parts = [vehicle.make, vehicle.model, vehicle.year ? String(vehicle.year) : undefined].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : <EmptyValue />;
}

const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-danger" role="alert">{message}</p> : null;

/** About tab (EQ-20, EQ-22): read-only label / value text, or one form with Save and Cancel. */
const EquipmentAboutTab: React.FC<EquipmentAboutTabProps> = ({
  vehicle,
  canEditUnit: mayEditUnit,
  canEditRoadClass: mayEditRoadClass,
  canEditCvip: mayEditCvip,
  editing,
  onUpdated,
  onCancel,
  onSaved,
  onDirtyChange
}) => {
  const canEditUnit = editing && mayEditUnit;
  const canEditRoadClass = editing && mayEditRoadClass;
  const canEditCvip = editing && mayEditCvip;
  const initial = useMemo(() => draftOf(vehicle), [vehicle]);
  const [draft, setDraft] = useState<Draft>(initial);
  const [profiles, setProfiles] = useState<VehicleProfile[]>([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setDraft(initial);
    setErrors({});
  }, [initial, editing]);

  const dirty = editing && JSON.stringify(draft) !== JSON.stringify(initial);
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!canEditUnit) return undefined;
    let cancelled = false;
    vehicleProfileService
      .getAllProfiles()
      .then((data) => {
        if (!cancelled) setProfiles(data);
      })
      .catch(() => {
        if (!cancelled) setErrors((e) => ({ ...e, vehicleProfileId: 'Could not load trip inspection profiles. Reload to try again.' }));
      });
    return () => {
      cancelled = true;
    };
  }, [canEditUnit]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    const nextErrors: Record<string, string> = {};
    if (draft.numberOfAxles !== '' && !/^\d+$/.test(draft.numberOfAxles)) nextErrors.numberOfAxles = 'Axles must be a whole number';
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      let next = vehicle;
      if (canEditUnit) {
        const profile = profiles.find((p) => p.id === draft.vehicleProfileId) ?? null;
        const changes: Partial<Vehicle> = {};
        if (draft.description !== initial.description) changes.description = draft.description.trim() || (null as unknown as string);
        if (draft.vehicleProfileId !== initial.vehicleProfileId) {
          changes.vehicleProfileId = draft.vehicleProfileId as number;
          changes.vehicleClassification = (profile?.inspection ?? null) as unknown as string;
        }
        if (draft.plateNumber !== initial.plateNumber) changes.plateNumber = draft.plateNumber.trim() || null;
        if (draft.numberOfAxles !== initial.numberOfAxles) {
          changes.numberOfAxles = (draft.numberOfAxles === '' ? null : Number(draft.numberOfAxles)) as number;
        }
        if (draft.comments !== initial.comments) changes.comments = draft.comments.trim() || (null as unknown as string);
        if (draft.gpsModemId !== initial.gpsModemId) changes.gpsModemId = draft.gpsModemId.trim() || null;
        if (Object.keys(changes).length > 0) next = await vehicleService.updateVehicle(vehicle.id, changes);
      }
      if (canEditRoadClass && draft.roadType && draft.roadType !== initial.roadType) {
        next = await vehicleService.setRoadClass(vehicle.id, { roadClass: draft.roadType === 'Off-Road' ? 'OFF_ROAD' : 'ON_ROAD' });
      }
      if (canEditCvip && draft.nextCVIP !== initial.nextCVIP) {
        next = await vehicleService.setNextCvip(vehicle.id, draft.nextCVIP || null);
      }
      onDirtyChange(false);
      onUpdated(next);
      onSaved();
    } catch (err) {
      setErrors(fieldErrorsOf(err));
    } finally {
      setSaving(false);
    }
  };

  const serviceAreas = (vehicle.vehicleServiceAreas ?? []).map((sa) => serviceAreaLabel(sa.serviceArea));
  const profileName = vehicle.vehicleProfile?.inspection || vehicle.vehicleClassification;

  const fields: Array<{ label: string; value: React.ReactNode; htmlFor?: string }> = [
    {
      label: 'Description',
      htmlFor: 'unit-description',
      value: canEditUnit ? (
        <Form.Control id="unit-description" as="textarea" rows={2} value={draft.description} onChange={(e) => set('description', e.target.value)} />
      ) : (
        text(vehicle.description)
      )
    },
    {
      label: 'Trip Inspection Profile',
      htmlFor: 'unit-profile',
      value: canEditUnit ? (
        <>
          <Form.Select
            id="unit-profile"
            value={draft.vehicleProfileId ?? ''}
            onChange={(e) => set('vehicleProfileId', e.target.value ? parseInt(e.target.value, 10) : null)}
          >
            <option value="">Not Set</option>
            {profiles.map((profile) => (
              <option key={profile.id} value={profile.id}>{profile.inspection}</option>
            ))}
          </Form.Select>
          <FieldError message={errors.vehicleProfileId} />
        </>
      ) : (
        text(profileName)
      )
    },
    { label: 'Vehicle Classification', value: text(vehicle.vehicleProfile?.vehicleClassification) },
    { label: 'Make / Model / Year', value: makeModelYear(vehicle) },
    { label: 'VIN', value: text(vehicle.vinNumber) },
    {
      label: 'Plate',
      htmlFor: 'unit-plate',
      value: canEditUnit ? (
        <>
          <Form.Control id="unit-plate" value={draft.plateNumber} onChange={(e) => set('plateNumber', e.target.value)} />
          <FieldError message={errors.plateNumber} />
        </>
      ) : (
        text(vehicle.plateNumber)
      )
    },
    {
      label: 'Axles',
      htmlFor: 'unit-axles',
      value: canEditUnit ? (
        <>
          <Form.Control
            id="unit-axles"
            inputMode="numeric"
            className="tw-max-w-[8rem]"
            value={draft.numberOfAxles}
            onChange={(e) => set('numberOfAxles', e.target.value)}
          />
          <FieldError message={errors.numberOfAxles} />
        </>
      ) : (
        text(vehicle.numberOfAxles)
      )
    },
    { label: 'Fuel Type', value: text(vehicle.fuelType) },
    { label: 'Summer Configuration', value: text(vehicle.summerConfiguration) },
    { label: 'Winter Configuration', value: text(vehicle.winterConfiguration) },
    { label: 'Foreman Area', value: text(vehicle.foremanArea?.name) },
    {
      label: 'Service Area',
      value: serviceAreas.length > 0 ? <span className="tw-whitespace-nowrap">{serviceAreas.join(', ')}</span> : <EmptyValue />
    },
    {
      label: 'On-Road / Off-Road',
      htmlFor: 'unit-road-type',
      value: canEditRoadClass ? (
        <>
          <Form.Select
            id="unit-road-type"
            className="tw-max-w-[12rem]"
            value={draft.roadType}
            onChange={(e) => set('roadType', e.target.value as Draft['roadType'])}
          >
            <option value="">Not Set</option>
            <option value="On-Road">{ROAD_TYPE_LABEL['On-Road']}</option>
            <option value="Off-Road">{ROAD_TYPE_LABEL['Off-Road']}</option>
          </Form.Select>
          <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-content-muted">Sets the fuel surcharge.</p>
          <FieldError message={errors.vehicleType || errors.roadClass} />
        </>
      ) : (
        text(roadTypeOf(vehicle))
      )
    },
    {
      label: 'GPS / Skyhawk Modem',
      htmlFor: 'unit-gps',
      value: canEditUnit ? (
        <Form.Control id="unit-gps" value={draft.gpsModemId} onChange={(e) => set('gpsModemId', e.target.value)} />
      ) : (
        text(vehicle.gpsModemId)
      )
    },
    {
      label: 'Next CVIP',
      htmlFor: 'unit-cvip',
      value: canEditCvip ? (
        <>
          <Form.Control
            id="unit-cvip"
            type="date"
            className="tw-max-w-[12rem]"
            value={draft.nextCVIP}
            onChange={(e) => set('nextCVIP', e.target.value)}
          />
          <FieldError message={errors.nextCVIP} />
        </>
      ) : (
        text(formatDate(vehicle.nextCVIP))
      )
    },
    {
      label: 'Comments',
      htmlFor: 'unit-comments',
      value: canEditUnit ? (
        <Form.Control id="unit-comments" as="textarea" rows={3} value={draft.comments} onChange={(e) => set('comments', e.target.value)} />
      ) : (
        <span className="tw-whitespace-pre-line">{text(vehicle.comments)}</span>
      )
    }
  ];

  const formError = errors._form;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Card>
        <Card.Header className="tw-flex tw-items-center tw-justify-between">
          <h2 className="tw-mb-0 tw-text-base tw-font-semibold">Unit Details</h2>
        </Card.Header>
        <dl className="tw-mb-0 tw-divide-y tw-divide-line">
          {fields.map((field) => (
            <div key={field.label} className="tw-grid tw-grid-cols-1 tw-gap-1 tw-px-4 tw-py-3 sm:tw-grid-cols-3 sm:tw-gap-4">
              <dt className="tw-text-sm tw-font-semibold tw-text-content-secondary">
                {editing && field.htmlFor ? <label htmlFor={field.htmlFor} className="tw-mb-0">{field.label}</label> : field.label}
              </dt>
              <dd className="tw-mb-0 tw-text-sm tw-text-content sm:tw-col-span-2">{field.value}</dd>
            </div>
          ))}
        </dl>
        {editing && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-line tw-px-4 tw-py-3">
            {formError && <p className="tw-mb-0 tw-mr-auto tw-text-sm tw-text-danger" role="alert">{formError}</p>}
            <Button size="sm" variant="outline-secondary" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </Card>
      <EquipmentInsuranceCard
        vehicleId={vehicle.id}
        insurances={vehicle.insurances || []}
        canEdit={editing && mayEditUnit}
        onChanged={async () => onUpdated(await vehicleService.getVehicleById(vehicle.id))}
      />
    </div>
  );
};

export default EquipmentAboutTab;
